import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMyCompanies, setActiveProduct } from '../store/slices/companySlice';

/**
 * Wraps protected routes. Manages a 3-step context: company → product → workflow.
 *
 * Steps:
 *  1. No active company → /pick-company
 *  2. Active company set, but company has multiple products and no product picked → / (dashboard chooser)
 *  3. Active company + product set → render the route
 *
 * Operator routes (/operator/*) bypass everything except superadmin gating elsewhere.
 */
export default function CompanyContextGuard({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { activeCompanyId, activeProduct, list, loading, hasFetched } = useSelector((s) => s.company);

  useEffect(() => {
    if (!hasFetched && !loading) {
      dispatch(fetchMyCompanies());
    }
  }, [dispatch, hasFetched, loading]);

  // Operator routes don't need a company context
  if (location.pathname.startsWith('/operator')) {
    return children;
  }

  // Wait for the company list to actually load before deciding anything
  if (!hasFetched || loading) return null;

  // Step 1: No active company → always go to picker (lets user choose between companies / operator panel)
  if (!activeCompanyId) {
    return <Navigate to="/pick-company" replace />;
  }

  // Find the active company
  const activeCompany = list.find((c) => c.id === activeCompanyId);
  if (!activeCompany) {
    // Active company not in user's list (e.g., subscription expired) → back to picker
    return <Navigate to="/pick-company" replace />;
  }

  const hasPR = !!activeCompany.has_press_review;
  const hasDCP = !!activeCompany.has_theatrical_dcp;
  const productCount = (hasPR ? 1 : 0) + (hasDCP ? 1 : 0);

  // Venue managers only operate on Theatrical DCP — force the active product
  // so they never land on the watermark side or get bounced through the
  // dashboard chooser (which would create a redirect loop with RoleGuard).
  if (activeCompany.role === 'venue_manager' && hasDCP && activeProduct !== 'theatrical_dcp') {
    dispatch(setActiveProduct('theatrical_dcp'));
    return null;
  }

  // Step 2: Auto-pick the only product if there's exactly one
  if (productCount === 1 && !activeProduct) {
    const only = hasPR ? 'press_review' : 'theatrical_dcp';
    dispatch(setActiveProduct(only));
    return null;  // Re-render after dispatch
  }

  // Step 3: Both products + no product picked → workspace-level routes are still allowed,
  // but product routes go back to the dashboard chooser.
  //
  // v10.19.11: campaigns + statistics span both products by design (a
  // film is one campaign whether its assets are watermark screeners or
  // theatrical DCPs), so they're product-agnostic. Without this entry
  // the guard bounced /campaigns → / for any two-product company, which
  // is what made the post-login landing flash the dashboard.
  const productAgnostic = (
    location.pathname === '/' ||
    location.pathname === '/profile' ||
    location.pathname === '/sources' ||
    location.pathname === '/campaigns' ||
    location.pathname === '/key-art' ||
    location.pathname.startsWith('/statistics/') ||
    location.pathname.startsWith('/settings/')
  );

  // v10.19.27: a campaign must be picked before product routes
  // unlock. The sidebar already hides those entries; this gate
  // catches direct URL navigation (bookmarks, manual address-bar
  // typing) by redirecting to /campaigns. Campaigns-agnostic
  // surfaces (Dashboard, profile, the picker pages themselves,
  // workspace settings) stay reachable so the operator can still
  // navigate while picking.
  // v10.19.30: Dashboard ('/') is also gated. Pre-campaign it'd just
  // show the "no campaign selected" prompt anyway, and the operator
  // should land on the campaigns picker as their first surface.
  const campaignAgnostic = (
    location.pathname === '/profile' ||
    location.pathname === '/campaigns' ||
    location.pathname.startsWith('/statistics/') ||
    location.pathname.startsWith('/settings/')
  );
  let hasCampaignPicked = false;
  try { hasCampaignPicked = !!localStorage.getItem('activeCampaignId'); } catch (_e) { /* */ }
  if (!hasCampaignPicked && !campaignAgnostic && activeCompany.role !== 'venue_manager') {
    return <Navigate to="/campaigns" replace />;
  }
  if (productCount >= 2 && !activeProduct && !productAgnostic) {
    // Venue managers should never see the watermark dashboard — send them
    // to a page they can actually use.
    if (activeCompany.role === 'venue_manager') {
      return <Navigate to="/library" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}
